import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  CheckCircle2,
  Copy,
  ExternalLink,
  Headset,
  LifeBuoy,
  Loader2,
  MessageSquareMore,
  Paperclip,
  Send,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";
import { collection, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase/client";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { usePatient } from "../contexts/PatientContext";
import { AppTab } from "../contexts/types";
import { PremiumActionButton, PremiumModuleCard, PremiumPageShell, PremiumSectionCard } from "./premium";
import type { LucideIcon } from "lucide-react";

type SupportKind = "technical" | "feedback" | "clinical" | "feature" | "contact";

type AttachmentItem = {
  name: string;
  url: string;
  mimeType: string;
  size?: number;
  kind: "attachment" | "screenshot";
};

type SupportFormState = {
  priority: string;
  title: string;
  description: string;
  impact: string;
  expectedFlow: string;
  returnEmail: string;
  moduleAffected: string;
  clinicalErrorType: string;
  clinicalImpact: string;
};

type SupportTemplate = {
  kind: SupportKind;
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  buttonKey: string;
  accentClass: string;
  examples: string[];
  defaultCategoryKey: string;
  defaultPriority: "medium" | "high" | "urgent";
};

const fileNameSanitize = (value: string) =>
  value.trim().replace(/[^\w.\-]+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "");

const formatBytes = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const detectBrowser = () => {
  if (typeof navigator === "undefined") return "Unknown";
  const ua = navigator.userAgent;
  if (/edg/i.test(ua)) return "Microsoft Edge";
  if (/chrome|chromium|crios/i.test(ua)) return "Google Chrome";
  if (/firefox|fxios/i.test(ua)) return "Mozilla Firefox";
  if (/safari/i.test(ua) && !/chrome|chromium|crios|edg/i.test(ua)) return "Safari";
  return "Browser";
};

const buildViewport = () => {
  if (typeof window === "undefined") return "unknown";
  return `${window.innerWidth}x${window.innerHeight}`;
};

const buildAppVersion = () => {
  const candidate = String(import.meta.env.VITE_APP_VERSION || "").trim();
  return candidate || "dev";
};

const moduleLabelKeys: Record<AppTab, string> = {
  [AppTab.PATIENTS]: "navPatients",
  [AppTab.AGENDA]: "navAgenda",
  [AppTab.REPORT_ANALYSIS]: "navReportAnalysis",
  [AppTab.DECISION]: "navProtocols",
  [AppTab.CONSULT]: "navConsult",
  [AppTab.ATLAS]: "navAtlas",
  [AppTab.BUREAUCRACY]: "navBureaucracy",
  [AppTab.CLINICAL_REGISTRY]: "navClinicalRegistry",
  [AppTab.SUPPORT_CENTER]: "navSupportCenter",
};

type SupportCenterProps = {
  currentModule: AppTab;
};

const attachmentInputAccept = "image/*,.pdf,.doc,.docx,.txt,.rtf,.csv,.zip";
const OFFICIAL_SUPPORT_EMAIL = "orthoprecisionapp@gmail.com";

const supportTabs: Array<{
  kind: SupportKind;
  labelKey: string;
  compactLabelKey: string;
}> = [
  { kind: "technical", labelKey: "supportCenterTabTechnical", compactLabelKey: "supportCenterTabTechnicalShort" },
  { kind: "feedback", labelKey: "supportCenterTabSuggestions", compactLabelKey: "supportCenterTabSuggestionsShort" },
  { kind: "clinical", labelKey: "supportCenterTabClinical", compactLabelKey: "supportCenterTabClinicalShort" },
  { kind: "feature", labelKey: "supportCenterTabFeatures", compactLabelKey: "supportCenterTabFeaturesShort" },
  { kind: "contact", labelKey: "supportCenterTabContact", compactLabelKey: "supportCenterTabContactShort" },
];

export function SupportCenterModule({ currentModule }: SupportCenterProps) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { activePatient } = usePatient();
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [template, setTemplate] = useState<SupportTemplate | null>(null);
  const [activeTopic, setActiveTopic] = useState<SupportKind>("technical");
  const [form, setForm] = useState<SupportFormState>({
    priority: "medium",
    title: "",
    description: "",
    impact: "",
    expectedFlow: "",
    returnEmail: "",
    moduleAffected: "",
    clinicalErrorType: "",
    clinicalImpact: "",
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedTicketId, setSubmittedTicketId] = useState("");
  const [submissionState, setSubmissionState] = useState<"idle" | "success">("idle");
  const [emailCopied, setEmailCopied] = useState(false);

  const currentModuleLabel = t(moduleLabelKeys[currentModule]);

  useEffect(() => {
    if (!drawerOpen || typeof document === "undefined") return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  const currentContext = useMemo(() => ({
    module: currentModuleLabel,
    language,
    browser: detectBrowser(),
    viewport: buildViewport(),
    appVersion: buildAppVersion(),
    path: typeof window === "undefined" ? "" : window.location.pathname,
    patientId: activePatient?.id || "",
    patientName: activePatient?.name || "",
  }), [activePatient?.id, activePatient?.name, currentModuleLabel, language]);

  const templates: SupportTemplate[] = [
    {
      kind: "technical",
      icon: Headset,
      titleKey: "supportCenterTechnicalTitle",
      descriptionKey: "supportCenterTechnicalDescription",
      buttonKey: "supportCenterTechnicalButton",
      accentClass: "border-cyan-400/15 bg-cyan-500/10",
      examples: [
        "supportCenterExampleUpload",
        "supportCenterExampleLogin",
        "supportCenterExampleImageGeneration",
        "supportCenterExamplePdf",
        "supportCenterExampleSlow",
        "supportCenterExampleViewer",
      ],
      defaultCategoryKey: "supportCenterCategoryTechnical",
      defaultPriority: "high",
    },
    {
      kind: "feedback",
      icon: MessageSquareMore,
      titleKey: "supportCenterFeedbackTitle",
      descriptionKey: "supportCenterFeedbackDescription",
      buttonKey: "supportCenterFeedbackButton",
      accentClass: "border-sky-400/15 bg-sky-500/10",
      examples: [
        "supportCenterExampleSuggestions",
        "supportCenterExampleUx",
        "supportCenterExampleImprovements",
        "supportCenterExampleIdeas",
      ],
      defaultCategoryKey: "supportCenterCategoryFeedback",
      defaultPriority: "medium",
    },
    {
      kind: "clinical",
      icon: ShieldAlert,
      titleKey: "supportCenterClinicalTitle",
      descriptionKey: "supportCenterClinicalDescription",
      buttonKey: "supportCenterClinicalButton",
      accentClass: "border-amber-400/15 bg-amber-500/10",
      examples: [
        "supportCenterExampleDiagnosisError",
        "supportCenterExampleClassificationError",
        "supportCenterExampleLocationError",
        "supportCenterExamplePlanError",
      ],
      defaultCategoryKey: "supportCenterCategoryClinical",
      defaultPriority: "urgent",
    },
    {
      kind: "feature",
      icon: Sparkles,
      titleKey: "supportCenterFeatureTitle",
      descriptionKey: "supportCenterFeatureDescription",
      buttonKey: "supportCenterFeatureButton",
      accentClass: "border-emerald-400/15 bg-emerald-500/10",
      examples: [
        "supportCenterExamplePacs",
        "supportCenterExampleDigitalSignature",
        "supportCenterExampleIntegration",
        "supportCenterExampleProtocols",
      ],
      defaultCategoryKey: "supportCenterCategoryFeature",
      defaultPriority: "medium",
    },
    {
      kind: "contact",
      icon: LifeBuoy,
      titleKey: "supportCenterContactTitle",
      descriptionKey: "supportCenterContactDescription",
      buttonKey: "supportCenterContactButton",
      accentClass: "border-fuchsia-400/15 bg-fuchsia-500/10",
      examples: [
        "supportCenterExampleContactBusiness",
        "supportCenterExampleContactTraining",
        "supportCenterExampleContactPartnership",
      ],
      defaultCategoryKey: "supportCenterCategoryContact",
      defaultPriority: "medium",
    },
  ];

  const activeTemplate = templates.find((item) => item.kind === activeTopic) || templates[0];

  const resetForm = (nextTemplate?: SupportTemplate | null) => {
    setTemplate(nextTemplate || null);
    setActiveTopic(nextTemplate?.kind || "technical");
    setAttachments([]);
    setScreenshot(null);
    setSubmitError("");
    setSubmissionState("idle");
    setSubmittedTicketId("");
    setForm({
      priority: nextTemplate ? nextTemplate.defaultPriority : "medium",
      title: "",
      description: "",
      impact: "",
      expectedFlow: "",
      returnEmail: user?.email || "",
      moduleAffected: currentModuleLabel,
      clinicalErrorType: nextTemplate?.kind === "clinical" ? t("supportCenterClinicalErrorDiagnosis") : "",
      clinicalImpact: "",
    });
  };

  const openDrawer = (nextTemplate: SupportTemplate) => {
    resetForm(nextTemplate);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => resetForm(null), 180);
  };

  const handleCardClick = (card: SupportTemplate) => {
    openDrawer(card);
  };

  const handleTopicChange = (kind: SupportKind) => {
    const nextTemplate = templates.find((item) => item.kind === kind) || null;
    setActiveTopic(kind);
    setTemplate(nextTemplate);
    setSubmitError("");
    if (nextTemplate && !form.priority) {
      setForm((current) => ({
        ...current,
        priority: nextTemplate.defaultPriority,
      }));
    }
  };

  const copySupportEmail = async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(OFFICIAL_SUPPORT_EMAIL);
        setEmailCopied(true);
        window.setTimeout(() => setEmailCopied(false), 1800);
      }
    } catch {
      // silent fallback; email remains visible
    }
  };

  const openSupportEmailClient = () => {
    if (typeof window === "undefined") return;
    window.location.href = `mailto:${OFFICIAL_SUPPORT_EMAIL}`;
  };

  const addAttachments = (files: FileList | File[] | null, kind: "attachment" | "screenshot") => {
    if (!files) return;
    const nextFiles = Array.from(files);
    if (kind === "screenshot" && nextFiles.length > 0) {
      setScreenshot(nextFiles[0]);
      return;
    }
    setAttachments((current) => [...current, ...nextFiles]);
  };

  const uploadFile = async (ticketId: string, file: File, kind: "attachment" | "screenshot") => {
    const safeName = fileNameSanitize(file.name || `${kind}-${Date.now()}`) || `${kind}-${Date.now()}`;
    const fileRef = ref(storage, `support_tickets/${user?.uid || "anonymous"}/${ticketId}/${kind}-${safeName}`);
    await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
    const url = await getDownloadURL(fileRef);
    return {
      name: file.name || safeName,
      url,
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      kind,
    } satisfies AttachmentItem;
  };

  const handleSubmit = async () => {
    const title = form.title.trim();
    const description = form.description.trim();
    const email = form.returnEmail.trim() || user?.email || "";
    const ticketRef = doc(collection(db, "supportTickets"));
    const selectedTemplate = template || activeTemplate;

    if (!selectedTemplate) return;
    if (!email) {
      setSubmitError(t("supportCenterReturnEmailRequired"));
      return;
    }
    if (!title || !description) {
      setSubmitError(t("supportCenterRequiredFields"));
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      const basePayload = {
        // unified fields (same as mobile)
        source: "web",
        uid: user?.uid || null,
        userEmail: user?.email || null,
        userName: user?.displayName || user?.email || null,
        platform: "web",
        appVersion: buildAppVersion(),
        status: "open",
        priority: form.priority || "medium",
        category: selectedTemplate.kind,
        relatedModule: form.moduleAffected || null,
        message: description,
        createdAt: serverTimestamp(),
        // web-specific fields
        kind: selectedTemplate.kind,
        title,
        description,
        returnEmail: email,
        moduleAffected: form.moduleAffected,
        clinicalErrorType: form.clinicalErrorType || "",
        clinicalImpact: form.clinicalImpact || "",
        impact: form.impact || "",
        expectedFlow: form.expectedFlow || "",
        context: currentContext,
        patientId: activePatient?.id || "",
        patientName: activePatient?.name || "",
        attachments: [] as AttachmentItem[],
        submittingStatus: "uploading",
      };

      await setDoc(ticketRef, basePayload);

      const allUploads: AttachmentItem[] = [];
      if (screenshot) {
        allUploads.push(await uploadFile(ticketRef.id, screenshot, "screenshot"));
      }

      for (const file of attachments) {
        allUploads.push(await uploadFile(ticketRef.id, file, "attachment"));
      }

      await updateDoc(ticketRef, {
        attachments: allUploads,
        submittingStatus: "submitted",
        submittedAt: serverTimestamp(),
      });

      setSubmissionState("success");
      setSubmittedTicketId(ticketRef.id);
      window.setTimeout(() => {
        closeDrawer();
      }, 1800);
    } catch (error) {
      console.error("[SupportCenter] submit failed", error);
      setSubmitError(t("supportCenterSubmitError"));
      try {
        await updateDoc(ticketRef, {
          submittingStatus: "error",
          submittedAt: serverTimestamp(),
        });
      } catch {
        // keep quiet; main error already shown
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const moduleOptions = [
    t("navPatients"),
    t("navReportAnalysis"),
    t("navProtocols"),
    t("navConsult"),
    t("navAtlas"),
    t("navBureaucracy"),
    t("navClinicalRegistry"),
    t("navSupportCenter"),
  ];

  const priorityOptions = [
    { value: "low", label: t("supportCenterPriorityLow") },
    { value: "medium", label: t("supportCenterPriorityMedium") },
    { value: "high", label: t("supportCenterPriorityHigh") },
    { value: "urgent", label: t("supportCenterPriorityUrgent") },
  ];

  const clinicalErrorOptions = [
    t("supportCenterClinicalErrorDiagnosis"),
    t("supportCenterClinicalErrorClassification"),
    t("supportCenterClinicalErrorAnatomy"),
    t("supportCenterClinicalErrorPlan"),
    t("supportCenterClinicalErrorOther"),
  ];

  return (
    <PremiumPageShell className="space-y-6 pb-4">
      <PremiumSectionCard
        title={t("supportCenterTitle")}
        subtitle={t("supportCenterSubtitle")}
        actions={
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-full border border-cyan-400/15 bg-cyan-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-200 sm:inline-flex">
              <Headset className="h-4 w-4" />
              {t("supportCenterEnterpriseBadge")}
            </div>
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-2 text-[10px] text-slate-300">
              <span className="hidden sm:inline">{t("supportCenterOfficialSupport")}:</span>
              <a href={`mailto:${OFFICIAL_SUPPORT_EMAIL}`} className="font-semibold text-cyan-200 transition hover:text-cyan-100">
                {OFFICIAL_SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        }
      >
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-white/8 bg-white/5 px-4 py-3 shadow-[0_0_20px_rgba(34,211,238,0.04)]">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">{t("supportCenterQuickResponseTitle")}</div>
            <p className="mt-1 text-xs leading-5 text-[color:var(--op-text-muted)]">{t("supportCenterQuickResponseBody")}</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/5 px-4 py-3 shadow-[0_0_20px_rgba(34,211,238,0.04)]">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">{t("supportCenterClinicalTitle")}</div>
            <p className="mt-1 text-xs leading-5 text-[color:var(--op-text-muted)]">{t("supportCenterClinicalPrecisionBody")}</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/5 px-4 py-3 shadow-[0_0_20px_rgba(34,211,238,0.04)]">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">{t("supportCenterContextTitle")}</div>
            <p className="mt-1 text-xs leading-5 text-[color:var(--op-text-muted)]">{t("supportCenterContextBody")}</p>
          </div>
        </div>
      </PremiumSectionCard>

      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {templates.map((card) => {
          const Icon = card.icon;
          return (
            <PremiumModuleCard
              key={card.kind}
              className="group cursor-pointer min-h-[176px] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-cyan-400/20 hover:shadow-[0_14px_40px_rgba(34,211,238,0.08)]"
              onClick={() => handleCardClick(card)}
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-2xl border ${card.accentClass} text-cyan-200 shadow-[0_0_20px_rgba(34,211,238,0.06)]`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 space-y-1.5">
                <h3 className="text-[16px] font-semibold text-[color:var(--op-text)]">{t(card.titleKey)}</h3>
                <p className="max-w-[34ch] truncate text-sm leading-6 text-[color:var(--op-text-muted)]">{t(card.descriptionKey)}</p>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 opacity-0 transition-opacity group-hover:opacity-100">
                  {t("supportCenterCardHint")}
                </div>
                <PremiumActionButton variant="secondary" icon={card.icon} className="px-3 py-2 text-[10px]">
                  {t(card.buttonKey)}
                </PremiumActionButton>
              </div>
            </PremiumModuleCard>
          );
        })}
      </div>

      <div className="rounded-[24px] border border-white/8 bg-[color:var(--op-card-bg-soft)] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">{t("supportCenterDisclaimerTitle")}</div>
            <p className="mt-1 max-w-[760px] text-xs leading-5 text-[color:var(--op-text-muted)]">{t("supportCenterDisclaimer")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            <span className="rounded-full border border-white/10 bg-black/20 px-3 py-2">{t("supportCenterOfficialSupport")}</span>
            <a
              href={`mailto:${OFFICIAL_SUPPORT_EMAIL}`}
              className="rounded-full border border-cyan-400/15 bg-cyan-500/10 px-3 py-2 text-cyan-200 transition hover:bg-cyan-500/15 hover:text-cyan-100"
            >
              {OFFICIAL_SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </div>

      {drawerOpen && template && (
        <div className="fixed inset-0 z-[180] bg-slate-950/60 backdrop-blur-sm">
          <button
            type="button"
            aria-label={t("supportCenterFormClose")}
            className="absolute inset-0 cursor-default"
            onClick={closeDrawer}
          />

          <div className="absolute inset-0 flex items-end justify-center p-0 md:items-start md:justify-end md:p-4">
            <div
              className="relative flex w-full max-h-[90vh] flex-col overflow-hidden rounded-t-[28px] border border-cyan-500/20 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_rgba(2,6,23,0.98)_45%)] shadow-[0_30px_120px_rgba(0,0,0,0.55)] md:w-[min(560px,calc(100vw-24px))] md:max-h-[calc(100vh-32px)] md:rounded-[30px]"
              style={{ width: "min(560px, calc(100vw - 24px))", maxHeight: "calc(100vh - 32px)" }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-white/5 px-4 py-4 sm:px-5">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-200/70">
                    {t("supportCenterDrawerTitle")}
                  </div>
                  <h3 className="mt-1 text-[20px] font-bold text-white">{t(template.titleKey)}</h3>
                  <p className="mt-1 text-[12px] leading-5 text-slate-300">{t("supportCenterDrawerSubtitle")}</p>
                </div>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition hover:bg-white/10"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
                {submissionState === "success" ? (
                  <div className="flex h-full min-h-[320px] items-center justify-center">
                    <div className="w-full max-w-md rounded-[28px] border border-emerald-400/20 bg-emerald-500/10 p-6 text-center shadow-[0_0_40px_rgba(16,185,129,0.12)]">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-emerald-300/25 bg-emerald-500/15 text-emerald-200 shadow-[0_0_28px_rgba(16,185,129,0.22)] animate-pulse">
                        <CheckCircle2 className="h-7 w-7" />
                      </div>
                      <h4 className="mt-4 text-[22px] font-bold text-white">{t("supportCenterSuccessTitle")}</h4>
                      <p className="mt-2 text-sm leading-6 text-slate-200">{t("supportCenterSuccessSubtitle")}</p>
                      <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left text-xs text-slate-200">
                        <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-200/80">
                          {t("supportCenterSuccessTicket")}
                        </span>
                        <span className="mt-1 block font-mono text-sm">{submittedTicketId}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {supportTabs.map((tab) => {
                        const isActive = tab.kind === activeTopic;
                        return (
                          <button
                            key={tab.kind}
                            type="button"
                            onClick={() => handleTopicChange(tab.kind)}
                            className={`shrink-0 rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] transition ${isActive ? "border-cyan-400/30 bg-cyan-500/15 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.08)]" : "border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-slate-200"}`}
                          >
                            <span className="hidden sm:inline">{t(tab.labelKey)}</span>
                            <span className="sm:hidden">{t(tab.compactLabelKey)}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">
                        {t(activeTemplate.titleKey)}
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[color:var(--op-text-muted)]">
                        {t(activeTemplate.descriptionKey)}
                      </p>
                    </div>

                    {activeTopic === "technical" && (
                      <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormPriority")}</span>
                            <select
                              value={form.priority}
                              onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
                              className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                            >
                              {priorityOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="hidden md:block" />
                        </div>

                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterTechnicalTitleField")}</span>
                          <input
                            value={form.title}
                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                            placeholder={t("supportCenterTechnicalTitlePlaceholder")}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>

                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterTechnicalDescriptionField")}</span>
                          <textarea
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            placeholder={t("supportCenterTechnicalDescriptionPlaceholder")}
                            rows={5}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>

                        <details className="rounded-[24px] border border-white/10 bg-black/15 p-4">
                          <summary className="cursor-pointer list-none text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">
                            {t("supportCenterAttachmentsAccordionTitle")}
                          </summary>
                          <div className="mt-4 rounded-[20px] border border-white/10 bg-white/5 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <p className="text-xs leading-5 text-slate-400">{t("supportCenterAttachmentHint")}</p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => screenshotInputRef.current?.click()}
                                  className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/15 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100"
                                >
                                  <Camera className="h-4 w-4" />
                                  {t("supportCenterFormAttachScreenshot")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200"
                                >
                                  <Paperclip className="h-4 w-4" />
                                  {t("supportCenterFormAddAttachments")}
                                </button>
                              </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {screenshot && (
                                <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold text-emerald-100">
                                  {t("supportCenterScreenshotLabel")}: {screenshot.name}
                                </span>
                              )}
                              {attachments.map((file, index) => (
                                <span key={`${file.name}-${index}`} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold text-slate-200">
                                  {file.name} {formatBytes(file.size) ? `- ${formatBytes(file.size)}` : ""}
                                </span>
                              ))}
                              {attachments.length === 0 && !screenshot && (
                                <span className="text-xs text-slate-500">{t("supportCenterNoAttachments")}</span>
                              )}
                            </div>
                          </div>
                        </details>
                      </div>
                    )}

                    {activeTopic === "feedback" && (
                      <div className="space-y-4">
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterSuggestionTitleField")}</span>
                          <input
                            value={form.title}
                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                            placeholder={t("supportCenterSuggestionTitlePlaceholder")}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterSuggestionDescriptionField")}</span>
                          <textarea
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            placeholder={t("supportCenterSuggestionDescriptionPlaceholder")}
                            rows={5}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterSuggestionImpactField")}</span>
                          <textarea
                            value={form.impact}
                            onChange={(event) => setForm((current) => ({ ...current, impact: event.target.value }))}
                            placeholder={t("supportCenterSuggestionImpactPlaceholder")}
                            rows={4}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                      </div>
                    )}

                    {activeTopic === "clinical" && (
                      <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormModuleAffected")}</span>
                            <select
                              value={form.moduleAffected}
                              onChange={(event) => setForm((current) => ({ ...current, moduleAffected: event.target.value }))}
                              className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                            >
                              {moduleOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="space-y-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormClinicalErrorType")}</span>
                            <select
                              value={form.clinicalErrorType}
                              onChange={(event) => setForm((current) => ({ ...current, clinicalErrorType: event.target.value }))}
                              className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                            >
                              <option value="">{t("supportCenterFormSelect")}</option>
                              {clinicalErrorOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterClinicalDescriptionField")}</span>
                          <textarea
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            placeholder={t("supportCenterClinicalDescriptionPlaceholder")}
                            rows={5}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormClinicalImpact")}</span>
                          <textarea
                            value={form.clinicalImpact}
                            onChange={(event) => setForm((current) => ({ ...current, clinicalImpact: event.target.value }))}
                            placeholder={t("supportCenterFormClinicalImpactPlaceholder")}
                            rows={4}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <details className="rounded-[24px] border border-white/10 bg-black/15 p-4">
                          <summary className="cursor-pointer list-none text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">
                            {t("supportCenterAttachmentsAccordionTitle")}
                          </summary>
                          <div className="mt-4 rounded-[20px] border border-white/10 bg-white/5 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <p className="text-xs leading-5 text-slate-400">{t("supportCenterAttachmentHint")}</p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => screenshotInputRef.current?.click()}
                                  className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/15 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100"
                                >
                                  <Camera className="h-4 w-4" />
                                  {t("supportCenterFormAttachScreenshot")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200"
                                >
                                  <Paperclip className="h-4 w-4" />
                                  {t("supportCenterFormAddAttachments")}
                                </button>
                              </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {screenshot && (
                                <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold text-emerald-100">
                                  {t("supportCenterScreenshotLabel")}: {screenshot.name}
                                </span>
                              )}
                              {attachments.map((file, index) => (
                                <span key={`${file.name}-${index}`} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold text-slate-200">
                                  {file.name} {formatBytes(file.size) ? `- ${formatBytes(file.size)}` : ""}
                                </span>
                              ))}
                              {attachments.length === 0 && !screenshot && (
                                <span className="text-xs text-slate-500">{t("supportCenterNoAttachments")}</span>
                              )}
                            </div>
                          </div>
                        </details>
                      </div>
                    )}

                    {activeTopic === "feature" && (
                      <div className="space-y-4">
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFeatureDesiredField")}</span>
                          <input
                            value={form.title}
                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                            placeholder={t("supportCenterFeatureDesiredPlaceholder")}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFeatureFlowField")}</span>
                          <textarea
                            value={form.expectedFlow}
                            onChange={(event) => setForm((current) => ({ ...current, expectedFlow: event.target.value }))}
                            placeholder={t("supportCenterFeatureFlowPlaceholder")}
                            rows={5}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormPriority")}</span>
                          <select
                            value={form.priority}
                            onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                          >
                            {priorityOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}

                    {activeTopic === "contact" && (
                      <div className="space-y-4">
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterContactMessageField")}</span>
                          <textarea
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            placeholder={t("supportCenterContactMessagePlaceholder")}
                            rows={5}
                            className="w-full rounded-3xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("supportCenterFormReturnEmail")}</span>
                          <input
                            value={form.returnEmail}
                            onChange={(event) => setForm((current) => ({ ...current, returnEmail: event.target.value }))}
                            placeholder={t("supportCenterFormReturnEmailPlaceholder")}
                            className="w-full rounded-2xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
                          />
                        </label>
                        <div className="rounded-[24px] border border-white/10 bg-black/20 p-4 text-center">
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                            {t("supportCenterOfficialSupport")}
                          </div>
                          <a
                            href={`mailto:${OFFICIAL_SUPPORT_EMAIL}`}
                            className="mt-1 block break-all text-sm font-semibold text-cyan-200 transition hover:text-cyan-100"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {OFFICIAL_SUPPORT_EMAIL}
                          </a>
                          <div className="mt-3 flex flex-wrap justify-center gap-2">
                            <button
                              type="button"
                              onClick={copySupportEmail}
                              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100 transition hover:bg-cyan-500/15"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              {emailCopied ? t("supportCenterEmailCopied") : t("supportCenterCopyEmail")}
                            </button>
                            <button
                              type="button"
                              onClick={openSupportEmailClient}
                              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              {t("supportCenterOpenEmailClient")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <details className="rounded-[24px] border border-cyan-400/15 bg-cyan-500/5 p-4">
                      <summary className="cursor-pointer list-none text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">
                        {t("supportCenterContextAccordionTitle")}
                      </summary>
                      <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-slate-200">
                          <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{t("supportCenterContextModule")}</span>
                          {currentContext.module}
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-slate-200">
                          <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{t("supportCenterContextLanguage")}</span>
                          {language.toUpperCase()}
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-slate-200">
                          <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{t("supportCenterContextBrowser")}</span>
                          {currentContext.browser}
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-slate-200">
                          <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{t("supportCenterContextViewport")}</span>
                          {currentContext.viewport}
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-slate-200 sm:col-span-2">
                          <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{t("supportCenterContextVersion")}</span>
                          {currentContext.appVersion}
                        </div>
                      </div>
                    </details>

                    {submitError && (
                      <div className="rounded-[20px] border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                        {submitError}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="shrink-0 border-t border-white/5 bg-slate-950/90 px-4 py-4 sm:px-5">
                <div className="mx-auto mb-3 flex max-w-[560px] flex-wrap items-center justify-center gap-2 rounded-[18px] border border-white/10 bg-black/15 px-4 py-3 text-xs text-slate-200">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                    {t("supportCenterOfficialSupport")}
                  </span>
                  <a
                    href={`mailto:${OFFICIAL_SUPPORT_EMAIL}`}
                    className="break-all font-semibold text-cyan-200 transition hover:text-cyan-100"
                  >
                    {OFFICIAL_SUPPORT_EMAIL}
                  </a>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={copySupportEmail}
                      className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100 transition hover:bg-cyan-500/15"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {emailCopied ? t("supportCenterEmailCopied") : t("supportCenterCopyEmail")}
                    </button>
                    <button
                      type="button"
                      onClick={openSupportEmailClient}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t("supportCenterOpenEmailClient")}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <PremiumActionButton variant="secondary" onClick={closeDrawer}>
                    {submissionState === "success" ? t("supportCenterFormClose") : t("supportCenterFormCancel")}
                  </PremiumActionButton>
                  {submissionState !== "success" && (
                    <PremiumActionButton
                      variant="primary"
                      icon={isSubmitting ? Loader2 : Send}
                      iconClassName={isSubmitting ? "animate-spin" : ""}
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? t("supportCenterFormSending") : t("supportCenterFormSubmit")}
                    </PremiumActionButton>
                  )}
                </div>
              </div>

              <input
                ref={screenshotInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(event) => {
                  addAttachments(event.target.files, "screenshot");
                  event.currentTarget.value = "";
                }}
              />
              <input
                ref={attachmentInputRef}
                type="file"
                className="hidden"
                multiple
                accept={attachmentInputAccept}
                onChange={(event) => {
                  addAttachments(event.target.files, "attachment");
                  event.currentTarget.value = "";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </PremiumPageShell>
  );
}

export default SupportCenterModule;
